import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import TopBar from "../../components/TopBar";
import MatrixBackground from "../../components/MatrixBackground";
import { NotifyButton } from "../../components/NotifyButton";
import { useBrowserNotifications, useNewItemsNotifier } from "../../hooks/useBrowserNotifications";
import { playChime, unlockChime } from "../../lib/chime";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Package, Plus, Edit3, Trash2, Check, X, Timer, Store } from "lucide-react";

const NEW_ORDER_CFG = {
  title: "New order on STACK",
  buildBody: (o) => `${o.customer_name} · ₹${o.total.toFixed(0)} · ${o.items.length} items`,
  tag: "merchant-order",
};

export default function MerchantDashboard() {
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [tab, setTab] = useState("orders"); // orders | products
  const [showNewShop, setShowNewShop] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);

  const [shopForm, setShopForm] = useState({ name: "", category: "Groceries", address: "", image_url: "", eta_minutes: 10 });
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch((e) => console.error("Categories load failed:", e));
  }, []);
  const CATEGORIES = categories.map((c) => c.name);
  const [prodForm, setProdForm] = useState({ name: "", category: "Groceries", price: 0, stock: 10, unit: "1 pc", image_url: "", description: "" });
  const [editingProd, setEditingProd] = useState(null);
  const { request, notify } = useBrowserNotifications();
  const placedOrders = useMemo(() => orders.filter((o) => o.status === "placed"), [orders]);
  const notifyWithChime = useCallback((title, body, tag) => {
    playChime();
    toast.info(title, { description: body });
    notify(title, body, tag);
  }, [notify]);
  useNewItemsNotifier(ordersLoaded ? placedOrders : null, NEW_ORDER_CFG, notifyWithChime);

  useEffect(() => {
    window.addEventListener("pointerdown", unlockChime);
    window.addEventListener("keydown", unlockChime);
    return () => {
      window.removeEventListener("pointerdown", unlockChime);
      window.removeEventListener("keydown", unlockChime);
    };
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [sRes, oRes] = await Promise.all([api.get("/merchant/my-shop"), api.get("/orders/mine")]);
      setShop(sRes.data || null);
      setOrders(oRes.data);
      setOrdersLoaded(true);
      if (sRes.data) {
        const pRes = await api.get("/merchant/products");
        setProducts(pRes.data);
      }
    } catch (e) {
      console.error("Merchant load failed:", e);
      toast.error("Failed to load shop data");
    }
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(() => {
      api.get("/orders/mine").then((r) => setOrders(r.data)).catch((e) => console.error("Order poll failed:", e));
    }, 5000);
    return () => clearInterval(t);
  }, [loadAll]);

  const createShop = async () => {
    try {
      const { data } = await api.post("/shops", shopForm);
      setShop(data);
      setShowNewShop(false);
      toast.success("Shop submitted. An admin will review it shortly.");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create shop"); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location isn't supported on this device");
    navigator.geolocation.getCurrentPosition(
      (p) => { setShopForm((f) => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude })); toast.success("Shop location captured"); },
      () => toast.error("Couldn't get your location. Allow location access and try again."),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const toggleShop = async () => {
    try {
      const { data } = await api.patch(`/shops/${shop.shop_id}/toggle`);
      setShop((s) => ({ ...s, is_online: data.is_online }));
    } catch (e) {
      console.error("Toggle shop failed:", e);
      toast.error("Could not update shop status");
    }
  };

  const saveProduct = async () => {
    try {
      if (editingProd) {
        await api.patch(`/products/${editingProd.product_id}`, prodForm);
        toast.success("Product updated");
      } else {
        await api.post(`/products?shop_id=${shop.shop_id}`, prodForm);
        toast.success("Product added");
      }
      setShowNewProduct(false);
      setEditingProd(null);
      setProdForm({ name: "", category: shop.category, price: 0, stock: 10, unit: "1 pc", image_url: "", description: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const deleteProduct = async (id) => {
    try { await api.delete(`/products/${id}`); loadAll(); toast.success("Removed"); }
    catch (e) { console.error("Delete product failed:", e); toast.error("Could not remove product"); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try { await api.patch(`/orders/${orderId}/status`, { status }); loadAll(); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const pendingOrders = orders.filter((o) => ["placed", "confirmed", "ready"].includes(o.status));
  const pastOrders = orders.filter((o) => !["placed", "confirmed", "ready"].includes(o.status));

  // No shop → onboarding
  if (!shop) {
    return (
      <div className="min-h-screen grid-bg isolate">
        <MatrixBackground opacity={0.2} />
        <TopBar />
        <div className="max-w-xl mx-auto px-4 pt-16" data-testid="merchant-onboarding">
          <div className="glass rounded-3xl p-8 ring-cyan">
            <Store size={28} className="text-cyan-300" />
            <h1 className="heading-font text-3xl font-extrabold mt-3">Register your shop</h1>
            <p className="text-slate-400 text-sm mt-1">Go live on STACK in under a minute.</p>
            <div className="space-y-4 mt-6">
              <Field label="Shop name"><Input value={shopForm.name} onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })} className="bg-white/5 border-cyan-500/15" data-testid="shop-name-input" /></Field>
              <Field label="Category">
                <Select value={shopForm.category} onValueChange={(v) => setShopForm({ ...shopForm, category: v })}>
                  <SelectTrigger className="bg-white/5 border-cyan-500/15" data-testid="shop-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Address"><Textarea value={shopForm.address} onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })} className="bg-white/5 border-cyan-500/15" rows={2} data-testid="shop-address-input" /></Field>
              <Button type="button" variant="outline" className="w-full bg-transparent border-cyan-500/25 text-cyan-300" onClick={useMyLocation} data-testid="shop-location-btn">
                {shopForm.lat ? `Location set (${shopForm.lat.toFixed(4)}, ${shopForm.lng.toFixed(4)})` : "Use my current location for the map"}
              </Button>
              <Field label="Cover image URL (optional)"><Input value={shopForm.image_url} onChange={(e) => setShopForm({ ...shopForm, image_url: e.target.value })} className="bg-white/5 border-cyan-500/15" placeholder="https://…" data-testid="shop-image-input" /></Field>
              <Field label="Prep + delivery ETA (mins)"><Input type="number" value={shopForm.eta_minutes} onChange={(e) => setShopForm({ ...shopForm, eta_minutes: parseInt(e.target.value || "10", 10) })} className="bg-white/5 border-cyan-500/15" data-testid="shop-eta-input" /></Field>
              <Button className="btn-neon w-full h-11 rounded-full" onClick={createShop} data-testid="create-shop-btn">Submit for approval</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-bg isolate pb-20" data-testid="merchant-dashboard">
      <MatrixBackground opacity={0.2} />
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        {/* Header */}
        <div className="glass rounded-3xl p-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Merchant Panel</div>
            <h1 className="heading-font text-3xl font-extrabold mt-1">{shop.name}</h1>
            <div className="text-slate-400 text-sm">{shop.address}</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <NotifyButton request={request} />
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${shop.is_online ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-slate-500/10 border-slate-500/30 text-slate-400"}`}>
              <span className={`w-2 h-2 rounded-full ${shop.is_online ? "bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.6)]" : "bg-slate-500"}`} />
              {shop.is_online ? "Online" : "Offline"}
            </div>
            <Switch checked={shop.is_online} onCheckedChange={toggleShop} data-testid="shop-online-toggle" />
          </div>
        </div>

        {!shop.is_approved && (
          <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="merchant-pending-approval">
            Your shop is waiting for admin approval. Customers will see it once it's approved. You can add products now.
          </div>
        )}

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-2">
          {["orders", "products"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`merchant-tab-${t}`} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t ? "btn-neon" : "bg-white/5 text-slate-300 border border-cyan-500/15"}`}>
              {t === "orders" ? `Orders (${pendingOrders.length})` : `Products (${products.length})`}
            </button>
          ))}
        </div>

        {/* ORDERS */}
        {tab === "orders" && (
          <div className="mt-5 grid gap-3">
            {pendingOrders.length === 0 && (
              <div className="glass rounded-2xl p-10 text-center text-slate-400">No live orders right now.</div>
            )}
            {pendingOrders.map((o) => (
              <div key={o.order_id} className="glass rounded-2xl p-4" data-testid={`merchant-order-${o.order_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Order #{o.order_id.slice(-6).toUpperCase()}</div>
                    <div className="heading-font font-bold text-lg">{o.customer_name}</div>
                    <div className="text-sm text-slate-400">{o.address}</div>
                  </div>
                  <div className="text-right">
                    <div className="heading-font text-2xl font-extrabold">₹{o.total.toFixed(2)}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1 justify-end">
                      <Timer size={11} className="text-cyan-400" /> {new Date(o.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="text-[11px] bg-white/5 border border-cyan-500/15 rounded-full px-2 py-0.5">
                      {it.name} × {it.qty}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  {o.status === "placed" && (
                    <>
                      <Button className="btn-neon rounded-full px-4 h-9" onClick={() => updateOrderStatus(o.order_id, "confirmed")} data-testid="merchant-accept-order-btn">
                        <Check size={14} className="mr-1" /> Accept
                      </Button>
                      <Button variant="outline" className="rounded-full px-4 h-9 border-rose-500/30 text-rose-300 hover:bg-rose-500/10" onClick={() => updateOrderStatus(o.order_id, "rejected")} data-testid="merchant-reject-order-btn">
                        <X size={14} className="mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {o.status === "confirmed" && (
                    <Button className="btn-neon rounded-full px-4 h-9" onClick={() => updateOrderStatus(o.order_id, "ready")} data-testid="merchant-ready-btn">
                      Mark Ready for Pickup
                    </Button>
                  )}
                  {o.status === "ready" && (
                    <div className="inline-flex items-center gap-2 text-sm text-cyan-300 font-semibold">
                      <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                      Awaiting rider pickup
                    </div>
                  )}
                </div>
              </div>
            ))}

            {pastOrders.length > 0 && (
              <>
                <div className="mt-6 heading-font text-sm font-bold text-slate-400 uppercase tracking-widest">Past orders</div>
                {pastOrders.slice(0, 12).map((o) => (
                  <div key={o.order_id} className="glass rounded-xl p-3 flex items-center justify-between text-sm">
                    <div className="min-w-0 truncate">
                      <span className="font-semibold">#{o.order_id.slice(-6).toUpperCase()}</span>
                      <span className="text-slate-500 ml-2">{o.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{o.status}</span>
                      <span className="font-bold">₹{o.total.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* PRODUCTS */}
        {tab === "products" && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-400 text-sm">{products.length} active SKUs</div>
              <Dialog open={showNewProduct} onOpenChange={setShowNewProduct}>
                <DialogTrigger asChild>
                  <Button className="btn-neon rounded-full px-4" onClick={() => { setEditingProd(null); setProdForm({ name: "", category: shop.category, price: 0, stock: 10, unit: "1 pc", image_url: "", description: "" }); }} data-testid="add-product-btn">
                    <Plus size={14} className="mr-1" /> Add product
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0E17] border-cyan-500/20 text-white">
                  <DialogHeader><DialogTitle className="heading-font text-xl">{editingProd ? "Edit" : "New"} product</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Field label="Name"><Input value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} className="bg-white/5 border-cyan-500/15" data-testid="product-name-input" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Price (₹)"><Input type="number" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: parseFloat(e.target.value || "0") })} className="bg-white/5 border-cyan-500/15" data-testid="product-price-input" /></Field>
                      <Field label="Stock"><Input type="number" value={prodForm.stock} onChange={(e) => setProdForm({ ...prodForm, stock: parseInt(e.target.value || "0", 10) })} className="bg-white/5 border-cyan-500/15" data-testid="product-stock-input" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Unit"><Input value={prodForm.unit} onChange={(e) => setProdForm({ ...prodForm, unit: e.target.value })} className="bg-white/5 border-cyan-500/15" placeholder="500g / 1pc" /></Field>
                      <Field label="Category">
                        <Select value={prodForm.category} onValueChange={(v) => setProdForm({ ...prodForm, category: v })}>
                          <SelectTrigger className="bg-white/5 border-cyan-500/15"><SelectValue /></SelectTrigger>
                          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                    </div>
                    <Field label="Image URL"><Input value={prodForm.image_url} onChange={(e) => setProdForm({ ...prodForm, image_url: e.target.value })} className="bg-white/5 border-cyan-500/15" placeholder="https://…" /></Field>
                    <Button className="btn-neon w-full rounded-full h-10" onClick={saveProduct} data-testid="save-product-btn">Save product</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {products.map((p) => (
                <div key={p.product_id} className="glass rounded-2xl overflow-hidden" data-testid={`merchant-product-${p.product_id}`}>
                  <div className="h-28 bg-slate-800">
                    {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-sm leading-tight line-clamp-1">{p.name}</div>
                    <div className="text-[11px] text-slate-500">{p.unit} · stock {p.stock}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="heading-font font-extrabold">₹{p.price}</div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingProd(p); setProdForm(p); setShowNewProduct(true); }} className="w-7 h-7 rounded-md bg-white/5 border border-cyan-500/15 inline-flex items-center justify-center text-slate-300 hover:text-cyan-300" data-testid={`edit-product-${p.product_id}`}>
                          <Edit3 size={12} />
                        </button>
                        <button onClick={() => deleteProduct(p.product_id)} className="w-7 h-7 rounded-md bg-white/5 border border-rose-500/15 inline-flex items-center justify-center text-rose-300 hover:text-rose-200" data-testid={`delete-product-${p.product_id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs uppercase tracking-widest font-bold">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
